// Bridge GitHub-style alert blockquotes to markstream-vue ::: admonitions.
//
//   > [!NOTE]            :::note
//   > body...     ->     body...
//                        :::
//
// markstream-vue does NOT parse `> [!NOTE]` natively - it renders as an ordinary
// blockquote with the literal "[!NOTE]" text. It DOES render `:::note` containers as
// styled admonitions. GitHub-flavored alerts are extremely common in agent output,
// README/SKILL.md content, and pasted docs, so we rewrite them at the content edge.
//
// Recognized markers map to markstream admonition kinds (note/info/tip/warning/danger/
// caution/error are the reserved container names): IMPORTANT has no kind of its own, so
// it maps to `info`; CAUTION maps to `caution`.
const ALERT_KIND: Record<string, string> = {
  note: 'note',
  tip: 'tip',
  important: 'info',
  warning: 'warning',
  caution: 'caution',
};

const MARKER = /^(\s*)>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*$/i;
const QUOTE_LINE = /^\s*>(.*)$/;
const FENCE = /^\s*(`{3,}|~{3,})/;

export function githubAlertsToAdmonitions(src: string): string {
  // Fast path: nothing that could be an alert marker.
  if (!src || !src.includes('[!')) return src;

  const lines = src.split('\n');
  const out: string[] = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    // Never rewrite inside a fenced code block (an agent may SHOW an alert example).
    if (FENCE.test(line)) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    const m = inFence ? null : MARKER.exec(line);
    if (!m) {
      out.push(line);
      continue;
    }
    const indent = m[1] ?? '';
    const kind = ALERT_KIND[(m[2] ?? '').toLowerCase()] ?? 'note';
    // Consume the contiguous blockquote body that follows the marker.
    const body: string[] = [];
    let j = i + 1;
    for (; j < lines.length; j++) {
      const bm = QUOTE_LINE.exec(lines[j] ?? '');
      if (!bm) break;
      body.push((bm[1] ?? '').replace(/^ /, '')); // strip one space after `>`
    }
    out.push(`${indent}:::${kind}`);
    for (const b of body) out.push(b);
    out.push(`${indent}:::`);
    i = j - 1;
  }
  return out.join('\n');
}
